import axios from 'axios';
import posthog from 'posthog-js';
import { store } from '../store';
import { logout } from '../store/authSlice';
import { queryClient } from '@/lib/queryClient';
import { PublicRoutePath } from '../config/routes';

const api = axios.create({
  baseURL: import.meta.env.VITE_API_URL || 'http://localhost:3000/api',
  headers: {
    'Content-Type': 'application/json',
  },
});

// Cache public pages
const flattenRoutes = (routes: Record<string, any>): string[] => {
  return Object.values(routes).reduce((acc: string[], value) => {
    if (typeof value === 'string') {
      acc.push(value);
    } else if (typeof value === 'object' && value !== null) {
      acc.push(...flattenRoutes(value));
    }
    return acc;
  }, [] as string[]);
};

const publicPages = flattenRoutes(PublicRoutePath)
  .filter((path) => path !== '/')
  .map((path) => new RegExp(path.replace(/:\w+/g, '.*')));

// Function to check if current page is public
const isPublicPage = (): boolean => {
  return publicPages.some((page) => page.test(window.location.pathname));
};

// Coach-cohort booking-creation and bookable-facilities lookups intentionally
// return 401 to mean "this caller is not authorized to act on this cohort
// member" (CoachCohortService.resolveCallerCoachId throws
// UnauthorizedException for e.g. "Only a coach can create a cohort booking"
// and "This member is not in your cohort") rather than "your session
// expired". A blanket logout on every 401 hides that message from an admin
// on the coach details page (they get bounced to /login before the modal can
// render anything). This predicate is a narrow, literal allowlist of just
// those two coach-cohort routes — every other 401 (including other
// coach-cohort endpoints) still triggers the normal logout + redirect below.
export const isBusinessRuleUnauthorized = (url?: string): boolean => {
  if (!url) return false;
  if (!url.includes('/coach-cohort/')) return false;
  return url.endsWith('/bookings/create') || url.endsWith('/bookable-facilities');
};

// Helper function for redirecting to login if needed
const redirectToLoginIfNeeded = () => {
  // Clear any stored auth data
  localStorage.removeItem('token');
  localStorage.removeItem('user');
  localStorage.removeItem('adminToken');
  localStorage.removeItem('adminUser');
  queryClient.clear();

  // Only redirect if we're not already on the login page or a public page
  if (!isPublicPage()) {
    window.location.href = '/login';
  }
};

// Add a request interceptor
api.interceptors.request.use(
  (config) => {
    const state = store.getState();
    // Check multiple token storage locations
    const token =
      sessionStorage.getItem('token') ||
      sessionStorage.getItem('adminToken') ||
      state.auth.token ||
      localStorage.getItem('token') ||
      localStorage.getItem('adminToken');

    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // Tie backend-captured PostHog exceptions to this frontend session/user
    if (import.meta.env.VITE_POSTHOG_KEY) {
      config.headers['x-posthog-distinct-id'] = posthog.get_distinct_id();
      config.headers['x-posthog-session-id'] = posthog.get_session_id();
    }

    return config;
  },
  (error) => Promise.reject(error)
);

// Add a response interceptor
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // If error is 401 and not from auth endpoints, redirect to login
    if (
      error.response?.status === 401 &&
      !originalRequest.url?.includes('/auth/login') &&
      !originalRequest.url?.includes('/auth/logout') &&
      !originalRequest.url?.includes('/register') &&
      !originalRequest.url?.includes('/register/member') &&
      !isBusinessRuleUnauthorized(originalRequest?.url)
    ) {
      // Clear auth data and redirect to login
      store.dispatch(logout());
      redirectToLoginIfNeeded();
    }

    return Promise.reject(error);
  }
);

export default api;
